import React, { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Check, RotateCcw, Sparkles } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STORAGE_KEY = "maqam_ritual_v1";

const EMPTY = {
  tawaf: [false, false, false, false, false, false, false],
  sai: [false, false, false, false, false, false, false],
  checklist: { ihram: false, tawaf: false, rakahs: false, zamzam: false, sai: false, hair: false },
};

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const p = JSON.parse(raw);
    return {
      tawaf: Array.isArray(p.tawaf) && p.tawaf.length === 7 ? p.tawaf : EMPTY.tawaf,
      sai: Array.isArray(p.sai) && p.sai.length === 7 ? p.sai : EMPTY.sai,
      checklist: { ...EMPTY.checklist, ...(p.checklist || {}) },
    };
  } catch {
    return EMPTY;
  }
}

export function useRitualProgress() {
  const [state, setState] = useState(load);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const toggleTawaf = useCallback((i) => {
    setState((s) => ({ ...s, tawaf: s.tawaf.map((v, idx) => (idx === i ? !v : v)) }));
  }, []);
  const toggleSai = useCallback((i) => {
    setState((s) => ({ ...s, sai: s.sai.map((v, idx) => (idx === i ? !v : v)) }));
  }, []);
  const toggleCheck = useCallback((key) => {
    setState((s) => ({ ...s, checklist: { ...s.checklist, [key]: !s.checklist[key] } }));
  }, []);
  const reset = useCallback(() => setState(EMPTY), []);

  return { state, toggleTawaf, toggleSai, toggleCheck, reset };
}

/* ---- shared cell (large tap target, navy + muted light blue) ---- */
const Cell = ({ checked, onClick, testid, children }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    aria-pressed={checked}
    className={`flex min-h-[64px] w-full items-center gap-3.5 rounded-2xl border-2 p-3.5 text-left transition-all duration-200 active:scale-[0.98] ${
      checked
        ? "border-[#B9D0EC] bg-[#EAF2FC]"
        : "border-slate-200 bg-white hover:border-[#B9D0EC]"
    }`}
  >
    <span
      className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl border-2 transition-colors duration-200 ${
        checked ? "border-[#6E97C7] bg-white" : "border-slate-300 bg-white"
      }`}
    >
      {checked && <Check size={20} strokeWidth={3} className="text-[#6E97C7]" />}
    </span>
    <span className="min-w-0 flex-1">{children}</span>
  </button>
);

const ToolCardUnused = null;

export const TawafCounter = ({ state, onToggle }) => {
  const { t } = useLang();
  const count = state.tawaf.filter(Boolean).length;
  const done = count === 7;
  return (
    <div className="maqam-card p-5" data-testid="tawaf-counter">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-bold text-[#0A192F]">{t("tawaf.title")}</h3>
        <span
          data-testid="tawaf-count"
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${done ? "bg-[#0A192F] text-[#E9CE7D]" : "bg-[#EAF2FC] text-[#3E6B9A]"}`}
        >
          {count} / 7
        </span>
      </div>
      <p className="mb-4 text-sm leading-relaxed text-slate-500">{t("tawaf.help")}</p>
      <div className="grid grid-cols-1 gap-2.5">
        {state.tawaf.map((checked, i) => (
          <Cell key={i} checked={checked} onClick={() => onToggle(i)} testid={`tawaf-circuit-${i + 1}`}>
            <span className="flex items-center gap-2">
              <span className="font-display text-base font-bold text-[#0A192F]">{i + 1}</span>
              <span className="text-sm text-slate-600">{t("tawaf.circuit")}</span>
            </span>
          </Cell>
        ))}
      </div>
      <p className="mt-4 text-center text-sm font-semibold" data-testid="tawaf-status">
        {done ? (
          <span className="inline-flex items-center gap-1.5 text-[#0A192F]">
            <Sparkles size={15} className="text-[#D4AF37]" /> {t("tawaf.done")}
          </span>
        ) : (
          <span className="text-[#3E6B9A]">{count} {t("tawaf.completed")}</span>
        )}
      </p>
    </div>
  );
};

const SAI_ROUTE = [
  ["Safa", "Marwa"],
  ["Marwa", "Safa"],
  ["Safa", "Marwa"],
  ["Marwa", "Safa"],
  ["Safa", "Marwa"],
  ["Marwa", "Safa"],
  ["Safa", "Marwa"],
];

export const SaiCounter = ({ state, onToggle }) => {
  const { t } = useLang();
  const count = state.sai.filter(Boolean).length;
  const done = count === 7;
  return (
    <div className="maqam-card p-5" data-testid="sai-counter">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-bold text-[#0A192F]">{t("sai.title")}</h3>
        <span
          data-testid="sai-count"
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${done ? "bg-[#0A192F] text-[#E9CE7D]" : "bg-[#EAF2FC] text-[#3E6B9A]"}`}
        >
          {count} / 7
        </span>
      </div>
      <p className="mb-4 text-sm leading-relaxed text-slate-500">{t("sai.help")}</p>
      <div className="grid grid-cols-1 gap-2.5">
        {state.sai.map((checked, i) => (
          <Cell key={i} checked={checked} onClick={() => onToggle(i)} testid={`sai-length-${i + 1}`}>
            <span className="flex items-center gap-2.5">
              <span className="font-display text-base font-bold text-[#0A192F]">{i + 1}</span>
              <span className="flex items-center gap-1.5 text-sm font-medium text-[#0A192F]">
                {SAI_ROUTE[i][0]}
                <span className="text-[#6E97C7]">→</span>
                {SAI_ROUTE[i][1]}
              </span>
            </span>
          </Cell>
        ))}
      </div>
      <div className="mt-3 rounded-xl bg-[#EAF2FC] px-3 py-2 text-center text-xs font-semibold text-[#3E6B9A]">
        {t("sai.finish")}
      </div>
      <p className="mt-3 text-center text-sm font-semibold" data-testid="sai-status">
        {done ? (
          <span className="inline-flex items-center gap-1.5 text-[#0A192F]">
            <Sparkles size={15} className="text-[#D4AF37]" /> {t("sai.done")}
          </span>
        ) : (
          <span className="text-[#3E6B9A]">{count} {t("sai.completed")}</span>
        )}
      </p>
    </div>
  );
};

const CHECK_ITEMS = [
  { key: "ihram", label: "checklist.ihram" },
  { key: "tawaf", label: "checklist.tawaf" },
  { key: "rakahs", label: "checklist.rakahs" },
  { key: "zamzam", label: "checklist.zamzam" },
  { key: "sai", label: "checklist.sai" },
  { key: "hair", label: "checklist.hair" },
];

export const RitualChecklist = ({ state, onToggle }) => {
  const { t } = useLang();
  const c = state.checklist;
  const complete = c.hair; // Umrah completes only after hair cutting
  return (
    <div className="maqam-card p-5" data-testid="ritual-checklist">
      <h3 className="mb-1 font-display text-lg font-bold text-[#0A192F]">{t("checklist.title")}</h3>
      <p className="mb-4 text-sm leading-relaxed text-slate-500">{t("checklist.help")}</p>
      <div className="grid grid-cols-1 gap-2.5">
        {CHECK_ITEMS.map(({ key, label }) => (
          <Cell key={key} checked={c[key]} onClick={() => onToggle(key)} testid={`checklist-${key}`}>
            <span className="text-sm font-medium text-[#0A192F]">{t(label)}</span>
          </Cell>
        ))}
      </div>
      {!complete && (
        <p className="mt-3 text-center text-xs text-slate-400">{t("checklist.hairNote")}</p>
      )}
      {complete && (
        <div
          data-testid="umrah-complete-banner"
          className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-[#0A192F] px-4 py-3.5 text-center"
        >
          <Sparkles size={18} className="text-[#D4AF37]" />
          <span className="text-sm font-bold text-white">{t("checklist.complete")}</span>
        </div>
      )}
    </div>
  );
};

export const ResetProgress = ({ onReset }) => {
  const { t } = useLang();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          data-testid="reset-progress-btn"
          className="mx-auto mt-2 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700 active:scale-95"
        >
          <RotateCcw size={15} /> {t("guide.reset")}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="reset-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("guide.resetTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("guide.resetBody")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reset-cancel">{t("guide.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            data-testid="reset-confirm"
            onClick={onReset}
            className="bg-[#B42318] hover:bg-[#9a1e14]"
          >
            {t("guide.reset")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
